import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import TourOverlay from './tour/TourOverlay';
import { getPageTour, isPageTourSeen, markPageTourSeen } from '../utils/pageTours';
import { useTranslation } from '../translations';

// Mini-przewodnik pojedynczej strony: przy pierwszym wejściu na daną sekcję
// tłumaczy, co gdzie jest. Treść i lista objętych stron — utils/pageTours.js.
// Auto-otwarcie tylko dla organizacji demo (jak spacer D.10) — stały klient nie
// ma dostawać wyskakujących okien w narzędziu, którego używa codziennie.
// Ponowne otwarcie (H.2): pozycja „Przewodnik po tej stronie” w menu przycisku „?”
// (components/help/HelpButton.js), dostępna dla KAŻDEGO zalogowanego. Własny przycisk
// „?” przewodnika zniknął: w rogu jest teraz jeden przycisk pomocy dla wszystkich.

// `mainTourOpen` — trwa spacer po aplikacji (D.10); `waitForMainTour` — App jeszcze nie
// rozstrzygnął, czy go otworzy (bez tego przewodnik strony głównej mignąłby przed spacerem).
// `openRequest` — licznik wyborów pozycji przewodnika w menu pomocy.
export default function PageTour({ user, mainTourOpen = false, waitForMainTour = false, storage, openRequest = 0 }) {
  const { t } = useTranslation();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const tour = getPageTour(location.pathname);
  const isDemo = !!user?.isDemo;
  const userId = user?.userId;
  const tourId = tour?.id;

  const store = storage || (typeof window !== 'undefined' ? window.localStorage : null);
  const suppressed = mainTourOpen || waitForMainTour;

  // Spacer przechodzi przez strony, które mają też własny przewodnik (Kalendarz, Magazyn,
  // Faktury, Strona główna). Strona, na której spacer właśnie zamknięto, dostaje swój
  // przewodnik dopiero przy NASTĘPNYM wejściu — inaczej po „Zaczynam" od razu wyskakiwał
  // drugi przewodnik tej samej strony. Liczone w trakcie renderu (wzorzec „stan z poprzedniego
  // renderu”), żeby efekt niżej widział odroczenie już w tej samej klatce.
  const [byloOtwarte, setByloOtwarte] = useState(mainTourOpen);
  const [odroczonaSciezka, setOdroczonaSciezka] = useState(null);
  if (byloOtwarte !== mainTourOpen) {
    setByloOtwarte(mainTourOpen);
    if (!mainTourOpen) setOdroczonaSciezka(location.pathname);
  }
  if (odroczonaSciezka && odroczonaSciezka !== location.pathname && !mainTourOpen) {
    setOdroczonaSciezka(null);
  }
  const odroczony = odroczonaSciezka === location.pathname;

  // Auto-otwarcie raz na stronę; wstrzymane, gdy trwa spacer (dwa przewodniki naraz
  // zasłaniałyby się nawzajem). Efekt „layout”, bo rozstrzyga przed narysowaniem klatki:
  // zwykły efekt pokazywał najpierw stronę bez przewodnika, a dopiero potem przewodnik.
  useLayoutEffect(() => {
    if (!tourId || !isDemo || suppressed || odroczony) { setOpen(false); return; }
    setOpen(!isPageTourSeen(store, userId, tourId));
  }, [tourId, isDemo, suppressed, odroczony, store, userId]);

  // Otwarcie z menu pomocy: licznik rośnie przy każdym wyborze pozycji. Obsłużoną wartość
  // pamiętamy, żeby przejście na inną stronę nie otwierało jej przewodnika samo z siebie.
  // Jawny wybór otwiera przewodnik także na stronie odroczonej po spacerze.
  const obsluzone = useRef(openRequest);
  useEffect(() => {
    if (openRequest === obsluzone.current) return;
    obsluzone.current = openRequest;
    if (tourId && !suppressed) setOpen(true);
  }, [openRequest, tourId, suppressed]);

  const close = useCallback(() => {
    if (tourId) markPageTourSeen(store, userId, tourId);
    setOpen(false);
  }, [store, tourId, userId]);

  // Klucze i18n z utils/pageTours.js rozwijamy TUTAJ, żeby `TourOverlay` dostał dokładnie
  // ten sam kształt kroku co dotąd (`title`/`lead`/`points` jako gotowe teksty). Dzięki temu
  // `utils/demoTour.js`, który dzieli z nim overlay i wciąż niesie literalny PL, działa bez
  // zmian — inaczej overlay musiałby rozróżniać dwa formaty kroku.
  const kroki = useMemo(() => (tour ? tour.steps.map((step) => ({
    ...step,
    title: t(step.titleKey),
    lead: t(step.leadKey),
    points: step.pointKeys.map((klucz) => t(klucz)),
  })) : []), [tour, t]);

  if (!tour || suppressed) return null;

  // `key`: przewodnik innej strony startuje od czystego stanu (krok 1, bez ramki),
  // zamiast przez klatkę pokazywać krok i podświetlenie strony poprzedniej.
  return (
    <TourOverlay
      key={tourId}
      open={open}
      steps={kroki}
      onClose={close}
      testId="page-tour"
      finishLabel={t('pageTours.finish')}
    />
  );
}
